import { useMemo, useState } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { SegmentedControl } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { useCurrentCampaignLocations } from "app/pages/digital-marketing/monitor-and-protect/services/api/campaign";
import { competitorsTracking } from "app/pages/digital-marketing/monitor-and-protect/services/tracking/competitors";
import { InfoTooltip } from "app/pages/digital-marketing/common/ui/InfoTooltip";
import { useTranslation } from "pro-features/components/WithTranslation";

import { Widget } from "../../../shared/ui/layout/widget";

import {
    VALUE_TYPE_OPTIONS,
    GRANULARITY_OPTIONS,
    GRANULARITY_MAP,
    getValueTypeIndex,
    getGranularityIndex,
} from "./assets";
import { ChartSkeleton } from "./chart-skeletion";
import { CompetitorsChart } from "./competitors-chart-component";
import { useCompetitorsSelectionManager } from "./hooks/useCompetitorsSelectionManager";
import type { GranularityType, ValueType } from "./types";
import { useCompetitorsChart } from "./use-competitors-chart";

export const CompetitorsChartWidget = () => {
    const [granularity, setGranularity] = useState<GranularityType>("Day");
    const [valueType, setValueType] = useState<ValueType>("absolute");
    const chart = useCompetitorsChart(granularity, valueType);
    const locations = useCurrentCampaignLocations();
    const selectionManager = useCompetitorsSelectionManager(chart.data);
    const translateMnp = useTranslation();

    const derivedState = useMemo(
        () => ({
            valueTypeIndex: getValueTypeIndex(valueType),
            granularityIndex: getGranularityIndex(granularity),
        }),
        [valueType, granularity],
    );

    return (
        <Widget>
            <Widget.Header>
                <Flex
                    gap="12px"
                    alignItems="center"
                >
                    <Text
                        fontVariant="h5_2"
                        color={colorsPalettes.carbon[500]}
                    >
                        {translateMnp("monitor_and_protect.overview.competitors_chart.title")}
                    </Text>
                    <InfoTooltip content={translate("monitor_and_protect.page.competitors.chart.title")} />
                </Flex>

                <Flex gap="12px">
                    <SegmentedControl
                        selectedIndex={derivedState.valueTypeIndex}
                        values={VALUE_TYPE_OPTIONS}
                        onChange={(index: number) => setValueType(index === 0 ? "absolute" : "relative")}
                    />
                    <SegmentedControl
                        selectedIndex={derivedState.granularityIndex}
                        values={GRANULARITY_OPTIONS}
                        onChange={(index: number) => {
                            const newGranularity = GRANULARITY_MAP[index];
                            setGranularity(newGranularity);
                            const subName =
                                newGranularity === "Day" ? "daily" : newGranularity === "Week" ? "weekly" : "monthly";
                            competitorsTracking.onGranularityChange(
                                subName,
                                locations.map((e) => e.countryCode),
                            );
                        }}
                    />
                </Flex>
            </Widget.Header>

            {chart.isLoading ? (
                <ChartSkeleton />
            ) : (
                <CompetitorsChart
                    granularity={granularity}
                    valueType={valueType}
                    chartData={selectionManager.chartData}
                    onSelectionChange={selectionManager.updateBulkSelection}
                />
            )}
        </Widget>
    );
};
